<template>
  <div class="dialog-launcher">
    <v-btn class="btn-leaf" @click="open">
      <v-icon left>mdi-account-plus-outline</v-icon>Utilizator nou
    </v-btn>
    <v-dialog v-model="dialog" persistent scrollable max-width="920" :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card>
        <v-form class="fdlg__form" @submit.prevent="saveUser">
          <div class="fdlg__head">
            <div>
              <h2 class="fdlg__title">Utilizator nou</h2>
              <p class="fdlg__sub">Parola se generează automat și se trimite pe e-mailul persoanei.</p>
            </div>
            <v-btn icon aria-label="Închide" @click="closeDialog()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <v-card-text ref="body" class="fdlg__body">
            <section class="form-section">
              <div class="form-section__label"><v-icon small>mdi-account-outline</v-icon>Persoana</div>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="firstName"
                    label="Numele*"
                    hint="de exemplu: Petricescu"
                    outlined
                    :error-messages="formErrors.firstName"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="lastName"
                    label="Prenumele*"
                    hint="de exemplu: Vasile"
                    outlined
                    :error-messages="formErrors.lastName"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="email"
                    label="E-mail*"
                    prepend-inner-icon="mdi-email-outline"
                    outlined
                    :error-messages="formErrors.email"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="telephone"
                    label="Telefon mobil"
                    prepend-inner-icon="mdi-cellphone"
                    outlined
                    :counter="9"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="landline"
                    label="Telefon fix*"
                    hint="3 cifre (interior) sau 9"
                    prepend-inner-icon="mdi-phone-outline"
                    outlined
                    :error-messages="formErrors.landline"
                  ></v-text-field>
                </v-col>
              </v-row>
            </section>

            <section class="form-section">
              <div class="form-section__label"><v-icon small>mdi-shield-account-outline</v-icon>Rolul în aplicație</div>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="selectedRole"
                    :items="roleItems"
                    label="Rol*"
                    item-text="label"
                    item-value="id"
                    outlined
                    :error-messages="formErrors.selectedRole"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-switch v-model="isActive" label="Contul este activ" inset color="primary"></v-switch>
                </v-col>
              </v-row>
            </section>

            <section class="form-section">
              <div class="form-section__label"><v-icon small>mdi-domain</v-icon>Departamente</div>
              <p class="form-section__note">
                O persoană poate lucra în mai multe departamente și poate fi șef în oricare dintre ele. Totul se vede într-un singur cont.
              </p>
              <DepartmentsEditor
                v-model="deptRows"
                :main-id.sync="mainDepartmentId"
                :departments="departmentOptions"
                :allow-sef="roleAllowsSef"
                :error="departmentsError"
              />
              <div class="mb-3"></div>
            </section>
          </v-card-text>

          <div class="fdlg__foot">
            <v-spacer></v-spacer>
            <v-btn text large @click="closeDialog()">Anulare</v-btn>
            <v-btn class="btn-green" large type="submit" :loading="saving">
              <v-icon left>mdi-content-save-outline</v-icon>Salvează
            </v-btn>
          </div>
        </v-form>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import * as api from "./api";
import * as helper from "@/helper/helper.js";
import DepartmentsEditor from "./DepartmentsEditor.vue";
import userDepartmentsMixin from "./userDepartmentsMixin";

export default {
  components: { DepartmentsEditor },
  mixins: [userDepartmentsMixin],
  data() {
    return {
      dialog: false,
      saving: false,
      roles: [],
      selectedRole: null,
      firstName: "",
      lastName: "",
      idnp: "",
      email: "",
      landline: "",
      telephone: "",
      isActive: true,
      formErrors: {
        selectedRole: "",
        firstName: "",
        lastName: "",
        email: "",
        telephone: "",
        landline: "",
      },
    };
  },
  methods: {
    open() {
      this.dialog = true;
      helper.scrollToTop(this);
    },
    async fetchData() {
      try {
        this.roles = await api.fetchRoles();
        await this.loadDepartmentOptions();
      } catch (error) {
        console.error("Error fetching roles:", error);
        this.$emit("error", "Rolurile sau departamentele nu au putut fi încărcate");
      }
    },
    async saveUser() {
      this.validateForm();
      const departmentsOk = this.validateDepartments();
      if (!this.isFormValid() || !departmentsOk) {
        return;
      }
      const formattedLandline = helper.validateLandline(this.landline);
      if (!formattedLandline) {
        this.formErrors.landline = "Telefon fix invalid";
        return;
      }

      const postData = {
        role_id: this.selectedRole,
        first_name: this.firstName,
        last_name: this.lastName,
        idnp: this.idnp,
        email: this.email,
        department: this.mainDepartmentName(),
        departments: this.departmentsPayload(),
        isActive: this.isActive,
        landline: formattedLandline,
        telephone: this.telephone,
      };

      this.saving = true;
      try {
        const created = await api.saveUserData(postData);
        // two people with the same name get "ion.popescu" and "ion.popescu2"
        const login = created && created.user_name ? ` Numele de utilizator: ${created.user_name}.` : "";
        this.$emit("success", `Utilizatorul a fost creat.${login} Parola i-a fost trimisă pe e-mail.`);
        this.$emit("updateUserData");
        this.dialog = false;
        this.clearFields();
      } catch (error) {
        console.error("Error saving user:", error);
        this.$emit("error", error.response?.data?.message || "Utilizatorul nu a fost creat. Verificați datele și încercați din nou.");
      } finally {
        this.saving = false;
      }
    },
    validateForm() {
      this.formErrors.selectedRole = this.selectedRole ? "" : "Alegeți un rol";
      this.formErrors.firstName = this.firstName ? "" : "Numele este obligatoriu";
      this.formErrors.lastName = this.lastName ? "" : "Prenumele este obligatoriu";
      this.formErrors.email = helper.validateEmail(this.email) ? "" : "Adresa de e-mail nu este validă";
    },
    isFormValid() {
      return !Object.values(this.formErrors).some((error) => error);
    },
    closeDialog() {
      this.dialog = false;
      this.clearFields();
    },
    clearFields() {
      this.formErrors = {
        selectedRole: "",
        firstName: "",
        lastName: "",
        email: "",
        telephone: "",
        landline: "",
      };
      this.selectedRole = null;
      this.firstName = "";
      this.lastName = "";
      this.idnp = "";
      this.email = "";
      this.telephone = "";
      this.landline = "";
      this.isActive = true;
      this.resetDepartments();
    },
  },
  mounted() {
    this.fetchData();
  },
  watch: {
    selectedRole() {
      this.formErrors.selectedRole = "";
    },
    firstName() {
      this.formErrors.firstName = "";
    },
    lastName() {
      this.formErrors.lastName = "";
    },
    email() {
      this.formErrors.email = "";
    },
    telephone() {
      this.formErrors.telephone = "";
    },
    landline() {
      this.formErrors.landline = "";
    },
  },
};
</script>
